import { useEffect, useState } from 'react';

import { DEFAULT_DEVICE_INFO, detectDeviceInfo } from './detect-device-info';

import type { DeviceInfo } from './detect-device-info';

/**
 * Single source of truth for OS / browser / device detection in WAC.
 *
 * Returns `DEFAULT_DEVICE_INFO` on the first render and populates the real
 * values in an effect. That is deliberate, for two reasons:
 *
 * 1. The server render has no `navigator`, so anything derived from it during
 *    the first render would mismatch on hydration.
 * 2. More importantly, the WAC edge cache key (see
 *    `utils/cache-helper/cache-helper.ts`) does not include the user agent, so
 *    server rendered markup is shared across device classes. Detecting during
 *    render would let a phone be served HTML built for a desktop, or vice versa.
 *
 * Callers must therefore treat "no device info yet" as a valid state — hide
 * device specific UI until it arrives rather than guessing.
 *
 * For viewport width, use `useIsMobile` instead. It answers a different
 * question and the two are not interchangeable.
 */
export const useDeviceInfo = (): DeviceInfo => {
	const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>(DEFAULT_DEVICE_INFO);

	useEffect(() => {
		if (typeof window === 'undefined' || !window.navigator) {
			return;
		}

		const { userAgent, platform, maxTouchPoints } = window.navigator;
		setDeviceInfo(detectDeviceInfo(userAgent, platform, maxTouchPoints));
	}, []);

	return deviceInfo;
};
